import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MachineRow } from "./MachineRow";
import { useDataContext } from "../../contexts/DataContext";
import {
  normalizeVenueMachine,
  resolveMachineById,
  getJobVenueId,
  coerceJsonArray,
} from "../../utils/jobMachines";

export function JobDetails({ job }) {
  const { machines } = useDataContext();
  const machine_ids_array = coerceJsonArray(job.machine_ids_array);
  const venue_machines = coerceJsonArray(job.venue_machines);
  const { log } = job;
  const [swappedMachines, setSwappedMachines] = useState([]);
  const [filteredMachines, setFilteredMachines] = useState([]);
  const [machinesAtVenue, setMachinesAtVenue] = useState([]);
  const [categorizedMachines, setCategorizedMachines] = useState({
    installed: [],
    removed: [],
    current: [],
    swapped: []
  }); 

  useEffect(() => {
    let filtered = [];
    if (
      machine_ids_array &&
      Array.isArray(machine_ids_array) &&
      machine_ids_array.length
    ) {
      filtered = machine_ids_array
        .map((id) => resolveMachineById(id, machines, venue_machines))
        .filter(Boolean);
      setFilteredMachines(filtered);
    } else {
      setFilteredMachines([]);
    }
  }, [machine_ids_array, machines, venue_machines, log]);

  useEffect(() => {
    try {
      const venueId = getJobVenueId(job);
      if (!venueId || !machine_ids_array) {
        setMachinesAtVenue([]);
        return;
      }
      const callIds = new Set(machine_ids_array.map(String));
      const merged = new Map();
      if (machines && Array.isArray(machines)) {
        machines
          .filter(
            (m) =>
              String(m.venue_id) === String(venueId) &&
              !callIds.has(String(m.machine_id_pk))
          )
          .forEach((m) => merged.set(String(m.machine_id_pk), m));
      }
      const vmList = venue_machines;
      vmList
        .filter(
          (m) =>
            String(m.venue_id) === String(venueId) &&
            !callIds.has(String(m.machine_id_pk))
        )
        .forEach((m) => {
          const row = normalizeVenueMachine(m);
          if (row) merged.set(String(row.machine_id_pk), row);
        });
      setMachinesAtVenue([...merged.values()]);
    } catch (error) {
      console.error("Error filtering machines at venue:", error);
      setMachinesAtVenue([]);
    }
  }, [machines, job, machine_ids_array, venue_machines]);

  const machineMovements = useMemo(() => {
    if (log && Array.isArray(log)) {
      return log.filter(
        (logItem) =>
          logItem.event === "machine_installed" ||
          logItem.event === "machine_removed" ||
          logItem.event === "machine_swapped"
      );
    }
    return [];
  }, [log]);

  useEffect(() => {
    if (!Array.isArray(machines)) return;

    if (!log || !Array.isArray(log)) {
      setCategorizedMachines({
        installed: [],
        removed: [],
        current: filteredMachines,
        swapped: [],
      });
      return;
    }

    {
      const installed = [];
      const removed = [];
      const current = [...filteredMachines];
      const swapped = [];

      const processedMachines = new Set();

      machineMovements.forEach((logItem) => {
        if (logItem.event === 'machine_installed' || (logItem.event === 'machine_moved' && logItem.details.installed_machine_id)) {
          const machineId = logItem.details.installed_machine_id || logItem.details.machine_id;
          const machine = resolveMachineById(machineId, machines, venue_machines);
          if (machine) {
            installed.push({ ...machine, installedAt: logItem.time });
            // Remove from current if it exists
            const index = current.findIndex(m => m.machine_id_pk === machine.machine_id_pk);
            if (index !== -1) current.splice(index, 1);
          }
          processedMachines.add(machineId);
        } else if (logItem.event === 'machine_removed' || (logItem.event === 'machine_moved' && logItem.details.machine_id)) {
          const machineId = logItem.details.removed_machine_id || logItem.details.machine_id;
          const machine = resolveMachineById(machineId, machines, venue_machines);
          if (machine) {
            removed.push({ ...machine, removedAt: logItem.time });
            // Remove from current if it exists
            const index = current.findIndex(m => m.machine_id_pk === machine.machine_id_pk);
            if (index !== -1) current.splice(index, 1);
          }
          processedMachines.add(machineId);
        } else if (logItem.event === 'machine_swapped') {
          if (!logItem.details.machine_to_venue || !logItem.details.machine_to_warehouse) {
            console.error("Invalid swap log item structure:", logItem);
            return; // Skip invalid log items
          }

          const inMachine = resolveMachineById(logItem.details.machine_to_venue, machines, venue_machines);
          const outMachine = resolveMachineById(logItem.details.machine_to_warehouse, machines, venue_machines);
          
          if (inMachine) {
            swapped.push({ ...inMachine, swappedIn: true, swappedAt: logItem.time });
            current.push(inMachine); // Add swapped-in machine to current
          }
          if (outMachine) {
            swapped.push({ ...outMachine, swappedIn: false, swappedAt: logItem.time });
            const index = current.findIndex(m => m.machine_id_pk === outMachine.machine_id_pk);
            if (index !== -1) current.splice(index, 1); // Remove swapped-out machine from current
          }
          processedMachines.add(logItem.details.machine_to_venue);
          processedMachines.add(logItem.details.machine_to_warehouse);
        }
      });

      // Filter out processed machines from the current list
      const updatedCurrent = current.filter(machine => !processedMachines.has(String(machine.machine_id_pk)));

      setCategorizedMachines({ installed, removed, current: updatedCurrent, swapped });
    }
  }, [machineMovements, machines, venue_machines, filteredMachines, log]);

  return (
    <>
      {categorizedMachines.installed.length > 0 && (
        <Box
          component="fieldset"
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "16px",
            marginTop: "20px",
          }}
        >
          <Typography component="legend" variant="h6" gutterBottom>
            Installed Machines
          </Typography>
          {categorizedMachines.installed.map((machine) => (
            <MachineRow
              key={machine.machine_id_pk}
              job={job}
              machine={machine}
              hasMoved={true}
              movementType="installed"
            />
          ))}
        </Box>
      )}

      {categorizedMachines.removed.length > 0 && (
        <Box
          component="fieldset"
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "16px",
            marginTop: "20px",
          }}
        >
          <Typography component="legend" variant="h6" gutterBottom>
            Removed Machines
          </Typography>
          {categorizedMachines.removed.map((machine) => (
            <MachineRow
              key={machine.machine_id_pk}
              machine={machine}
              hasMoved={true}
              movementType="removed"
            />
          ))}
        </Box>
      )}

      {categorizedMachines.current.length > 0 && (
        <Box
          component="fieldset"
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "16px",
            marginTop: "20px",
          }}
        >
          <Typography component="legend" variant="h6" gutterBottom>
            Machines in this call
          </Typography>
          {categorizedMachines.current.map((machine) => (
            <MachineRow
              key={machine.machine_id_pk}
              job={job}
              machine={machine}
              hasMoved={false}
            />
          ))}
        </Box>
      )}

      {categorizedMachines.swapped.length > 0 && (
        <Box
          component="fieldset"
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "16px",
            marginTop: "20px",
          }}
        >
          <Typography component="legend" variant="h6" gutterBottom>
            Swapped Machines
          </Typography>
          {categorizedMachines.swapped.map((machine) => (
            <MachineRow
              key={machine.machine_id_pk}
              job={job}
              machine={machine}
              hasMoved={true}
              movementType={machine.swappedIn ? "Swapped In" : "Swapped Out"}
            />
          ))}
        </Box>
      )}

      {machinesAtVenue.length > 0 && (
        <Accordion
          sx={{
            marginTop: "20px",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="other-machines-content"
            id="other-machines-header"
          >
            <Typography variant="h6">
              Other Machines at This Venue ({machinesAtVenue.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "16px",
              }}
            >
              {machinesAtVenue.map((machine) => (
                <MachineRow
                  key={machine.machine_id_pk}
                  job={job}
                  machine={machine}
                  hasMoved={false}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
